import {
  EvaluationEvent,
  UpdateEvaluationEventDto,
} from '@keyops-cep/api-client';
import { format } from 'date-fns';
import { z } from 'zod';

import { sanitizeString } from '../../utils/functions/common-utils';

const formatDateTimeLocal = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");

export const evaluationEventEditFormSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    startDate: z
      .string()
      .min(1, 'Start date is required')
      .refine((val) => !isNaN(new Date(val).getTime()), {
        message: 'Invalid start date',
      }),
    endDate: z
      .string()
      .min(1, 'End date is required')
      .refine((val) => !isNaN(new Date(val).getTime()), {
        message: 'Invalid end date',
      }),
    learningObjectives: z.array(
      z.object({ name: z.string().min(1, 'Learning objective is required') })
    ),
    locationName: z.string().optional().or(z.literal('')),
    address: z.string().optional().or(z.literal('')),
    city: z.string().optional().or(z.literal('')),
    province: z.string().optional().or(z.literal('')),
    postalCode: z.string().optional().or(z.literal('')),
    mealType: z.string().optional().or(z.literal('')),
    language: z.enum(['en', 'fr']),
    speakers: z
      .array(z.object({ name: z.string().min(1, 'Speaker name is required') }))
      .optional(),
    moderators: z
      .array(
        z.object({ name: z.string().min(1, 'Moderator name is required') })
      )
      .optional(),
  })
  .refine(
    (data) =>
      new Date(data.endDate).getTime() > new Date(data.startDate).getTime(),
    {
      path: ['endDate'],
      message: 'End date must be after start date',
    }
  );

export type EvaluationEventEditFormData = z.infer<
  typeof evaluationEventEditFormSchema
>;

export const getDefaultEvaluationEventEditFormValues =
  (): EvaluationEventEditFormData => ({
    name: '',
    startDate: '',
    endDate: '',
    learningObjectives: [],
    locationName: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    mealType: '',
    speakers: [],
    moderators: [],
    language: 'en',
  });

export const mapEventDataToFormDefaults = (
  eventData: EvaluationEvent
): EvaluationEventEditFormData => {
  return {
    name: eventData.name ?? '',
    startDate: eventData.startDate
      ? formatDateTimeLocal(new Date(eventData.startDate))
      : '',
    endDate: eventData.endDate
      ? formatDateTimeLocal(new Date(eventData.endDate))
      : '',
    locationName: eventData.locationName ?? '',
    address: eventData.address ?? '',
    city: eventData.city ?? '',
    province: eventData.province ?? '',
    postalCode: eventData.postalCode ?? '',
    mealType: eventData.mealType ?? '',
    learningObjectives:
      eventData.learningObjectives?.map((obj) => ({ name: obj })) ?? [],
    speakers: eventData.speakers?.map((s) => ({ name: s.name })) ?? [],
    moderators: eventData.moderators?.map((m) => ({ name: m.name })) ?? [],
    language: eventData.language === 'fr' ? 'fr' : 'en',
  };
};

export const transformFormValues = (
  values: EvaluationEventEditFormData
): UpdateEvaluationEventDto => {
  return {
    name: sanitizeString(values.name),
    startDate: values.startDate ? new Date(values.startDate) : undefined,
    endDate: values.endDate ? new Date(values.endDate) : undefined,
    locationName: sanitizeString(values.locationName),
    address: sanitizeString(values.address),
    city: sanitizeString(values.city),
    province: sanitizeString(values.province),
    postalCode: sanitizeString(values.postalCode),
    mealType: sanitizeString(values.mealType),
    learningObjectives:
      values.learningObjectives
        ?.map((obj) => sanitizeString(obj.name))
        .filter((s): s is string => !!s) ?? [],
    speakers: values.speakers ?? [],
    moderators: values.moderators ?? [],
    language: values.language,
  };
};
